import React, { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import Breadcrumb from "../components/Breadcrumb";
import {
  getPendencias,
  deleteFoto,
  deleteDocumento,
} from "../services/pendenciaService";
import { getCustomers } from "../services/customerService";
import { getUsers } from "../services/userService";
import PendenciaTable from "../components/PendenciaTable";
import PendenciaForm from "../components/PendenciaForm";
import {
  AddButton,
  Container,
  Title,
  TableWrapper,
  HeaderRow,
  PaginationButton,
  PageCounter,
  SearchInput,
} from "../styles/StyledComponents";

// Função auxiliar para formatar a data
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

const PendenciaManagementPage = () => {
  const [pendencias, setPendencias] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  const [formVisible, setFormVisible] = useState(false);
  const [editingPendencia, setEditingPendencia] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [pendenciasData, customerData, userData] = await Promise.all([
        getPendencias(currentPage, { searchQuery }),
        getCustomers(1, 100),
        getUsers(),
      ]);

      const enrichedPendencias = pendenciasData.pendencias.map((p) => {
        const cliente = customerData.customers.find((c) =>
          c.equipment.some((e) => e.equipmentNumber === p.equipamentoNumero)
        );
        return {
          ...p,
          tipoContrato: cliente?.tipoContrato || "-",
        };
      });

      setPendencias(enrichedPendencias);
      setTotalPages(pendenciasData.totalPages);
      setCustomers(customerData.customers);
      setTechnicians(userData.filter((u) => u.profile === "Tecnico"));
    } catch (error) {
      console.error("Erro ao carregar dados iniciais:", error);
    }
  }, [currentPage, searchQuery]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleEditPendencia = (pendencia) => {
    const cliente = customers.find((c) =>
      c.equipment.some((e) => e.equipmentNumber === pendencia.equipamentoNumero)
    );

    const tecnico = technicians.find(
      (t) => String(t._id) === String(pendencia.tecnico?._id || pendencia.tecnico)
    );

    const tecnicoFechamento = technicians.find(
      (t) =>
        String(t._id) ===
        String(pendencia.tecnicoFechamento?._id || pendencia.tecnicoFechamento)
    );

    setEditingPendencia({
      ...pendencia,
      clienteId: cliente?._id || undefined,
      tecnico: tecnico?._id || undefined,
      dataApontamento: formatDate(pendencia.dataApontamento),
      tecnicoFechamento: tecnicoFechamento?._id || undefined,
      dataFechamento: formatDate(pendencia.dataFechamento),
    });

    setFormVisible(true);
  };

  const handleAddNewPendencia = () => {
    setEditingPendencia({
      clienteId: "",
      equipamentoNumero: "",
      tecnico: "",
      dataApontamento: "",
      tecnicoFechamento: "",
      dataFechamento: "",
      situacaoEquipamento: "Funcionando",
      statusPendencia: "Nova",
      tpPendencia: "",
      descricaoPendencia: "",
      notasPendencia: "",
      obsFechamentoPendencia: "",
    });
    setFormVisible(true);
  };

  const handleDeletePhoto = async (fotoNome) => {
    if (!editingPendencia || !editingPendencia._id) return;
    try {
      const data = await deleteFoto(editingPendencia._id, fotoNome);
      setEditingPendencia((prev) => ({ ...prev, fotos: data.fotos }));
      await fetchAll();
    } catch (error) {
      console.error("Erro ao deletar a foto:", error);
    }
  };

  const handleDeleteDocumento = async (docNome) => {
    if (!editingPendencia || !editingPendencia._id) return;
    try {
      const data = await deleteDocumento(editingPendencia._id, docNome);
      setEditingPendencia((prev) => ({ ...prev, documentos: data.documentos }));
      await fetchAll();
    } catch (error) {
      console.error("Erro ao deletar o documento:", error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditingPendencia((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Layout pageTitle="Gerenciamento de Pendências">
      <Breadcrumb items={["Home", "Pendências", "Lista de Pendências"]} />
      <Container>
        <TableWrapper>
          <HeaderRow>
            <Title>Cadastro de Pendências</Title>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <SearchInput
                type="text"
                placeholder="Buscar por equipamento ou técnico"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <AddButton onClick={handleAddNewPendencia}>+ Nova Pendência</AddButton>
            </div>
          </HeaderRow>
          <PendenciaTable pendencias={pendencias} onEdit={handleEditPendencia} />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
            <PaginationButton onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              Anterior
            </PaginationButton>
            <PageCounter>
              Página {currentPage} de {totalPages}
            </PageCounter>
            <PaginationButton onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              Próxima
            </PaginationButton>
          </div>
        </TableWrapper>
        <PendenciaForm
          key={editingPendencia?._id || "new"}
          visible={formVisible}
          initialData={editingPendencia || {}}
          onCancel={() => setFormVisible(false)}
          isEditing={!!editingPendencia?._id}
          onChange={handleFormChange}
          onSubmit={null}
          onFilesChange={() => {}} // Pode ser substituído por função real, se necessário
          onDeletePhoto={handleDeletePhoto}
          onDeleteDocumento={handleDeleteDocumento}
          onSuccess={(nova) => {
            setFormVisible(false);
            setEditingPendencia(null);
            fetchAll();
          }}
        />
      </Container>
    </Layout>
  );
};

export default PendenciaManagementPage;
